import { useEffect, useState } from 'react';
import {
  DEFAULT_GAME_OPTIONS,
  GAME_OPTION_COPY,
  GameOptions,
  PLAYER_COUNT_OPTIONS,
  SCORE_TO_WIN_OPTIONS,
  TABLE_TITLE_MAX_LENGTH,
} from 'shared';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Text } from '../../ui/Text';
import { token } from '../../ui/theme';

/*
 * CreateGameModal — configuration prompt shown before opening a new table. The
 * host names the game (the title the lobby lists), picks the score to win, the
 * player count, and public (listed in the lobby) vs private (invite-link only).
 * "Advanced rules" holds the optional rule variants (GameOptions), collapsed by
 * default so the common case stays a three-choice form. All are saved onto the
 * table at creation.
 */

type ScoreOption = (typeof SCORE_TO_WIN_OPTIONS)[number];
type PlayerCountOption = (typeof PLAYER_COUNT_OPTIONS)[number];

interface Props {
  open: boolean;
  busy?: boolean;
  suggestedTitle?: string;
  onCancel: () => void;
  onCreate: (config: {
    title: string;
    scoreToWin: ScoreOption;
    isPrivate: boolean;
    numberOfPlayers: PlayerCountOption;
    options: GameOptions;
  }) => void;
}

// One rule variant: a switch plus the shared explanation of what it changes.
function RuleToggle({
  rule,
  checked,
  onChange,
}: {
  rule: keyof GameOptions;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { label, detail } = GAME_OPTION_COPY[rule];
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className="ui-bare-btn"
      onClick={() => onChange(!checked)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        cursor: 'pointer',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${checked ? token('color-gold') : token('color-border')}`,
        background: token('color-bg'),
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: 'none',
          width: 34,
          height: 20,
          marginTop: 2,
          borderRadius: 'var(--radius-pill)',
          background: checked ? token('color-gold') : token('color-border'),
          transition: 'background var(--dur-fast) var(--ease-out)',
        }}
      >
        <span
          style={{
            display: 'block',
            width: 16,
            height: 16,
            margin: 2,
            borderRadius: 'var(--radius-pill)',
            background: checked ? token('color-on-gold') : token('color-surface-2'),
            transform: checked ? 'translateX(14px)' : 'none',
            transition: 'transform var(--dur-fast) var(--ease-out)',
          }}
        />
      </span>
      <span>
        <Text as="div" style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          {label}
        </Text>
        <Text as="div" variant="muted" style={{ fontSize: 'var(--font-size-xs)' }}>
          {detail}
        </Text>
      </span>
    </button>
  );
}

// A single-select segmented control (radio-group semantics) styled to the UI kit.
function OptionGroup<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <Text as="div" variant="label" style={{ marginBottom: 'var(--space-2)', letterSpacing: 1 }}>
        {label}
      </Text>
      <div role="radiogroup" aria-label={label} style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              style={{
                flex: 1,
                cursor: 'pointer',
                padding: '10px 0',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${selected ? token('color-gold') : token('color-border')}`,
                background: selected ? 'rgba(212,175,55,0.12)' : token('color-bg'),
                color: selected ? token('color-gold') : token('color-text'),
                fontWeight: selected ? 700 : 400,
                fontFamily: 'inherit',
                fontSize: 'var(--font-size-sm)',
                transition: 'all var(--dur-fast) var(--ease-out)',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export const CreateGameModal = ({ open, busy, suggestedTitle, onCancel, onCreate }: Props) => {
  const [title, setTitle] = useState('');
  const [scoreToWin, setScoreToWin] = useState<ScoreOption>(11);
  const [numberOfPlayers, setNumberOfPlayers] = useState<PlayerCountOption>(4);
  const [isPrivate, setIsPrivate] = useState(false);
  const [options, setOptions] = useState<GameOptions>(DEFAULT_GAME_OPTIONS);
  const [rulesOpen, setRulesOpen] = useState(false);

  // Reseed on open so cancel-then-reopen starts from the defaults, not a
  // half-typed name or last time's rules.
  useEffect(() => {
    if (!open) return;
    setTitle(suggestedTitle ?? '');
    setOptions(DEFAULT_GAME_OPTIONS);
    setRulesOpen(false);
  }, [open, suggestedTitle]);

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onCancel}
      title="New Game"
      actions={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onCreate({ title, scoreToWin, isPrivate, numberOfPlayers, options })}
            disabled={busy}
          >
            {busy ? 'Creating…' : 'Create Game'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 20 }}>
        <div>
          <label htmlFor="game-title">
            <Text variant="label" style={{ letterSpacing: 1 }}>
              Game name
            </Text>
          </label>
          <Input
            id="game-title"
            value={title}
            maxLength={TABLE_TITLE_MAX_LENGTH}
            placeholder="Friday Night Setback"
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
            style={{
              width: '100%',
              marginTop: 'var(--space-2)',
              boxSizing: 'border-box',
              padding: '10px 12px',
            }}
          />
          <Text as="div" variant="muted" style={{ fontSize: '0.75rem', marginTop: 6 }}>
            Shown in the lobby so friends can spot your game.
          </Text>
        </div>
        <OptionGroup<PlayerCountOption>
          label="PLAYERS"
          value={numberOfPlayers}
          onChange={setNumberOfPlayers}
          options={PLAYER_COUNT_OPTIONS.map((n) => ({
            value: n,
            label: `${n} (${n / 2} teams)`,
          }))}
        />
        <OptionGroup<ScoreOption>
          label="SCORE TO WIN"
          value={scoreToWin}
          onChange={setScoreToWin}
          options={SCORE_TO_WIN_OPTIONS.map((s) => ({
            value: s,
            label: String(s),
          }))}
        />
        <OptionGroup<'public' | 'private'>
          label="VISIBILITY"
          value={isPrivate ? 'private' : 'public'}
          onChange={(v) => setIsPrivate(v === 'private')}
          options={[
            { value: 'public', label: 'Public' },
            { value: 'private', label: 'Private' },
          ]}
        />
        <Text variant="muted" style={{ fontSize: '0.75rem' }}>
          {isPrivate
            ? 'Private games are hidden from the lobby. Share the invite link to let others join.'
            : 'Public games appear in the lobby for anyone to join.'}
        </Text>

        <div>
          <button
            type="button"
            className="ui-bare-btn"
            aria-expanded={rulesOpen}
            onClick={() => setRulesOpen(!rulesOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 12,
                color: token('color-muted'),
                transition: 'transform var(--dur-fast) var(--ease-out)',
                transform: rulesOpen ? 'rotate(90deg)' : 'none',
              }}
            >
              ▶
            </span>
            <Text variant="label" style={{ letterSpacing: 1 }}>
              Advanced rules
            </Text>
          </button>

          {rulesOpen && (
            <div style={{ display: 'grid', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              {(Object.keys(GAME_OPTION_COPY) as (keyof GameOptions)[]).map((rule) => (
                <RuleToggle
                  key={rule}
                  rule={rule}
                  checked={options[rule]}
                  onChange={(checked) => setOptions({ ...options, [rule]: checked })}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
